import type { CSSProperties, FocusEvent, KeyboardEvent, MouseEvent, ReactNode, RefObject } from 'react';
import { forwardRef, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MoreHorizIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import { Checkbox } from 'components/ui/Checkbox';
import { useActiveNavigationIndicator, useDelayedCommitValue } from 'components/ui/hooks';
import {
  getNavigationActionContentPosition,
  type NavigationActionVisualState,
  useNavigationActionContentWidth,
  useNavigationActionFeedback,
} from 'components/ui/navigationActionFeedback';
import { OverflowTooltip } from 'components/ui/OverflowTooltip';
import { Skeleton } from 'components/ui/Skeleton';
import { Tooltip } from 'components/ui/Tooltip';
import { getKeyboardNavigationIndex, isKeyboardActivationKey, } from 'components/ui/utilities/keyboardNavigation';
import { getSearchHighlightSegments } from 'components/ui/utilities/searchHighlight';
import { getZoomAdjustedPx } from 'components/ui/utilities/zoom';
import { DEFAULT_ZOOM_LEVEL } from 'context/ZoomContext';
import { PrismThemeProvider } from 'theme';
import type {
  TertiaryNavigationActionButtonProps,
  TertiaryNavigationActionTransitionProps,
  TertiaryNavigationAiButtonProps,
  TertiaryNavigationCheckboxActionProps,
  TertiaryNavigationCurrencySelectorProps,
  TertiaryNavigationIconButtonProps,
  TertiaryNavigationItemProps,
  TertiaryNavigationProps,
  TertiaryNavigationSearchProps,
  TertiaryNavigationSearchSuggestionPillProps,
  TertiaryNavigationSkeletonProps,
  TertiaryNavigationSplitActionButtonProps,
} from './TertiaryNavigation.props';
import {
  TertiaryNavigationActionButtonRoot,
  TertiaryNavigationActions,
  TertiaryNavigationActionTransitionContent,
  TertiaryNavigationActionTransitionRoot,
  TertiaryNavigationActiveIndicator,
  TertiaryNavigationAiButtonRoot,
  TertiaryNavigationAiIcon,
  TertiaryNavigationCheckboxActionLabel,
  TertiaryNavigationCheckboxActionRoot,
  TertiaryNavigationCurrencySegment,
  TertiaryNavigationCurrencySelectorRoot,
  TertiaryNavigationCurrencyUnit,
  TertiaryNavigationCurrencyValue,
  TertiaryNavigationIconButtonRoot,
  TertiaryNavigationIconTooltipAnchor,
  TertiaryNavigationItemCloseButton,
  TertiaryNavigationItemLabel,
  TertiaryNavigationItemMenuButton,
  TertiaryNavigationItemRoot,
  TertiaryNavigationItems,
  TertiaryNavigationItemsArea,
  TertiaryNavigationLeftActions,
  TertiaryNavigationRoot,
  TertiaryNavigationScrollButton,
  TertiaryNavigationSearchClearButton,
  TertiaryNavigationSearchIcon,
  TertiaryNavigationSearchInput,
  TertiaryNavigationSearchLoader,
  TertiaryNavigationSearchRoot,
  TertiaryNavigationSearchSubmitHint,
  TertiaryNavigationSearchSuggestionContent,
  TertiaryNavigationSearchSuggestionDescription,
  TertiaryNavigationSearchSuggestionHighlight,
  TertiaryNavigationSearchSuggestionIcon,
  TertiaryNavigationSearchSuggestionItem,
  TertiaryNavigationSearchSuggestionLabel,
  TertiaryNavigationSearchSuggestionMetadata,
  TertiaryNavigationSearchSuggestionPillRoot,
  TertiaryNavigationSearchSuggestions,
  TertiaryNavigationSearchSuggestionsStatus,
  TertiaryNavigationSearchTrailingActions,
  TertiaryNavigationSplitActionRoot,
  TertiaryNavigationSplitContentItem,
  TertiaryNavigationSplitMainButton,
  TertiaryNavigationSplitMainContent,
  TertiaryNavigationSplitMenuButton,
  TertiaryNavigationSplitSavedCheck,
  TertiaryNavigationSplitSavedCheckPath,
  TertiaryNavigationSplitStatusIcon,
} from './TertiaryNavigation.styles';
import { TERTIARY_NAVIGATION_TEST_IDS } from './TertiaryNavigation.testIds';

const SCALAR_AI_ICON_SRC = '/images/scalar_ai_white.png';
const TERTIARY_NAVIGATION_SCROLL_STEP_PX = 240;
const TERTIARY_NAVIGATION_SEARCH_SUBMIT_HINT_ACTIVE_MS = 80;
const createSkeletonItems = (count: number) => Array.from({length: count}, (_, index) => index);
const getTextTooltipTitle = (content: ReactNode) => (typeof content === 'string' ? content : '');

const getTertiaryNavigationActionStatusIcon = (visualState: NavigationActionVisualState) => {
  if (visualState === 'saving') {
    return (
      <TertiaryNavigationSplitStatusIcon aria-hidden="true">
        <CircularProgress color="inherit" size="inherit" thickness={5} />
      </TertiaryNavigationSplitStatusIcon>
    );
  }

  if (visualState === 'saved') {
    return (
      <TertiaryNavigationSplitStatusIcon aria-hidden="true">
        <TertiaryNavigationSplitSavedCheck viewBox="0 0 16 16">
          <TertiaryNavigationSplitSavedCheckPath d="M3 8.25 6.5 11.5 13 5" pathLength={1} />
        </TertiaryNavigationSplitSavedCheck>
      </TertiaryNavigationSplitStatusIcon>
    );
  }

  if (visualState === 'error') {
    return (
      <TertiaryNavigationSplitStatusIcon aria-hidden="true">
        <ErrorOutlineIcon fontSize="inherit" />
      </TertiaryNavigationSplitStatusIcon>
    );
  }

  return null;
};

interface TertiaryNavigationActionContentSlotsProps {
  children: ReactNode;
  contentWidth: number | null;
  errorContentRef: RefObject<HTMLSpanElement | null>;
  errorLabel: ReactNode;
  idleContentRef: RefObject<HTMLSpanElement | null>;
  savedContentRef: RefObject<HTMLSpanElement | null>;
  savedLabel: ReactNode;
  savingContentRef: RefObject<HTMLSpanElement | null>;
  savingLabel: ReactNode;
  visualState: NavigationActionVisualState;
}

const TertiaryNavigationActionContentSlots = ({
  children,
  contentWidth,
  errorContentRef,
  errorLabel,
  idleContentRef,
  savedContentRef,
  savedLabel,
  savingContentRef,
  savingLabel,
  visualState,
}: TertiaryNavigationActionContentSlotsProps) => (
  <TertiaryNavigationSplitMainContent style={contentWidth ? { width: contentWidth } : undefined}>
    <TertiaryNavigationSplitContentItem
      ref={idleContentRef}
      contentPosition={getNavigationActionContentPosition(visualState, 'idle')}>
      {children}
    </TertiaryNavigationSplitContentItem>
    <TertiaryNavigationSplitContentItem
      ref={savingContentRef}
      contentPosition={getNavigationActionContentPosition(visualState, 'saving')}>
      {savingLabel}
    </TertiaryNavigationSplitContentItem>
    <TertiaryNavigationSplitContentItem
      ref={savedContentRef}
      contentPosition={getNavigationActionContentPosition(visualState, 'saved')}>
      {savedLabel}
    </TertiaryNavigationSplitContentItem>
    <TertiaryNavigationSplitContentItem
      ref={errorContentRef}
      contentPosition={getNavigationActionContentPosition(visualState, 'error')}>
      {errorLabel}
    </TertiaryNavigationSplitContentItem>
  </TertiaryNavigationSplitMainContent>
);

const renderHighlightedSearchText = (text: string, highlightText: string) => {
  const segments = getSearchHighlightSegments(text, highlightText);

  if (!segments.some(segment => segment.isHighlighted)) {
    return text;
  }

  return segments.map(segment => {
    if (!segment.isHighlighted) {
      return segment.text;
    }

    return (
      <TertiaryNavigationSearchSuggestionHighlight key={`${segment.startIndex}-${segment.text}`}>
        {segment.text}
      </TertiaryNavigationSearchSuggestionHighlight>
    );
  });
};
const getSafeZoomLevel = (zoomLevel: number) => (
  Number.isFinite(zoomLevel) && zoomLevel > 0 ? zoomLevel : DEFAULT_ZOOM_LEVEL
);

const getAdjustedScrollStepPx = (zoomLevel: number) => TERTIARY_NAVIGATION_SCROLL_STEP_PX / getSafeZoomLevel(zoomLevel);

const TertiaryNavigationItem = forwardRef<HTMLDivElement, TertiaryNavigationItemProps>(({
  closeAriaLabel,
  disabled,
  focusable,
  hasError,
  isClosable,
  label,
  onItemClose,
  onKeyboardNavigate,
  onItemMenuClick,
  onSelect,
  selected,
  showMenu,
  value,
  zoomLevel,
  ...props
}, ref) => {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (isKeyboardActivationKey(event.key)) {
      event.preventDefault();
      onSelect?.();
      return;
    }

    onKeyboardNavigate?.(event, value);
  };

  return (
    <TertiaryNavigationItemRoot
      ref={ref}
      aria-disabled={disabled || undefined}
      aria-selected={selected}
      data-testid={TERTIARY_NAVIGATION_TEST_IDS.item(value)}
      isDisabled={disabled}
      isSelected={selected}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={disabled ? undefined : handleKeyDown}
      role="tab"
      tabIndex={disabled || !focusable ? -1 : 0}
      zoomLevel={zoomLevel}
      {...props}>
      <OverflowTooltip title={getTextTooltipTitle(label)}>
        <TertiaryNavigationItemLabel>{label}</TertiaryNavigationItemLabel>
      </OverflowTooltip>
      {hasError && (
        <ErrorOutlineIcon
          aria-label="has validation errors"
          color="error"
          sx={{flexShrink: 0, fontSize: 'var(--tertiary-navigation-icon-size, 16px)'}}
        />
      )}
      {showMenu && (
        <TertiaryNavigationItemMenuButton
          aria-label={`Open ${typeof label === 'string' ? label : 'item'} menu`}
          data-testid={TERTIARY_NAVIGATION_TEST_IDS.itemMenu(value)}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onItemMenuClick?.(event);
          }}
          type="button">
          <MoreHorizIcon fontSize="inherit"/>
        </TertiaryNavigationItemMenuButton>
      )}
      {isClosable && (
        <TertiaryNavigationItemCloseButton
          aria-label={closeAriaLabel ?? `Close ${typeof label === 'string' ? label : 'item'} tab`}
          data-testid={TERTIARY_NAVIGATION_TEST_IDS.itemClose(value)}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onItemClose?.(event);
          }}
          type="button">
          <CloseIcon fontSize="inherit"/>
        </TertiaryNavigationItemCloseButton>
      )}
    </TertiaryNavigationItemRoot>
  );
});

TertiaryNavigationItem.displayName = 'TertiaryNavigationItem';

const TertiaryNavigation = ({
  actions,
  ariaLabel = 'Tertiary navigation',
  items = [],
  leftActions,
  onChange,
  onItemClose,
  onItemMenuClick,
  value = false,
  zoomLevel = DEFAULT_ZOOM_LEVEL,
}: TertiaryNavigationProps) => {
  const itemRefs = useRef(new Map<string, HTMLDivElement>());
  const itemsViewportRef = useRef<HTMLDivElement | null>(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
    hasOverflow: false,
  });

  const enabledItems = useMemo(
    () => items.filter(item => !item.disabled),
    [items]
  );
  const itemValues = useMemo(() => items.map(item => item.value), [items]);
  const {
    setActiveIndicatorRef,
    setActiveItemRef,
  } = useActiveNavigationIndicator({
    activeValue: value,
    containerRef: itemsViewportRef,
    isIndicatorInScrollContent: true,
    itemValues,
  });

  const focusableValue = useMemo(() => {
    if (value !== false && enabledItems.some(item => item.value === value)) {
      return value;
    }

    return enabledItems[0]?.value ?? false;
  }, [enabledItems, value]);

  const setItemRef = useCallback(
    (itemValue: string) => (node: HTMLDivElement | null) => {
      setActiveItemRef(itemValue)(node);

      if (node) {
        itemRefs.current.set(itemValue, node);
        return;
      }

      itemRefs.current.delete(itemValue);
    },
    [setActiveItemRef]
  );

  const updateScrollState = useCallback(() => {
    const viewport = itemsViewportRef.current;

    if (!viewport) {
      return;
    }

    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const nextScrollState = {
      canScrollLeft: viewport.scrollLeft > 1,
      canScrollRight: viewport.scrollLeft < maxScrollLeft - 1,
      hasOverflow: maxScrollLeft > 1,
    };

    setScrollState(currentScrollState => (
      currentScrollState.canScrollLeft === nextScrollState.canScrollLeft
            && currentScrollState.canScrollRight === nextScrollState.canScrollRight
            && currentScrollState.hasOverflow === nextScrollState.hasOverflow
        ? currentScrollState
        : nextScrollState
    ));
  }, []);

  const requestScrollStateUpdate = useCallback(() => {
    if (typeof window === 'undefined') {
      updateScrollState();
      return;
    }

    window.requestAnimationFrame(updateScrollState);
  }, [updateScrollState]);

  const scrollItemIntoView = useCallback((itemValue: string) => {
    itemRefs.current.get(itemValue)?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    });
    requestScrollStateUpdate();
  }, [requestScrollStateUpdate]);

  // Overflow arrows depend on rendered tab geometry, so measure after layout and when zoom changes.
  useLayoutEffect(() => {
    const viewport = itemsViewportRef.current;

    if (!viewport) {
      return undefined;
    }

    updateScrollState();
    viewport.addEventListener('scroll', updateScrollState, {passive: true});

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateScrollState);
      return () => {
        viewport.removeEventListener('scroll', updateScrollState);
        window.removeEventListener('resize', updateScrollState);
      };
    }

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(viewport);

    return () => {
      viewport.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [items, leftActions, updateScrollState, zoomLevel]);

  // Keep URL/query-driven active tabs visible when the selected value changes outside this primitive.
  useEffect(() => {
    if (value === false) {
      return;
    }

    scrollItemIntoView(value);
  }, [items, scrollItemIntoView, value]);

  const handleItemsScroll = useCallback((direction: 'left' | 'right') => {
    const viewport = itemsViewportRef.current;

    if (!viewport) {
      return;
    }

    viewport.scrollBy({
      behavior: 'smooth',
      left: getAdjustedScrollStepPx(zoomLevel) * (direction === 'left' ? -1 : 1),
    });
    requestScrollStateUpdate();
  }, [requestScrollStateUpdate, zoomLevel]);

  // Tertiary tabs are custom elements, so they own roving focus instead of relying on MUI Tabs.
  const handleKeyboardNavigate = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, currentValue: string) => {
      const currentIndex = enabledItems.findIndex(item => item.value === currentValue);
      const nextIndex = getKeyboardNavigationIndex({
        currentIndex,
        itemCount: enabledItems.length,
        key: event.key,
        orientation: 'horizontal',
      });
      const nextItem = enabledItems[nextIndex];

      if (!nextItem) {
        return;
      }

      event.preventDefault();
      itemRefs.current.get(nextItem.value)?.focus();
      scrollItemIntoView(nextItem.value);
    },
    [enabledItems, scrollItemIntoView]
  );

  return (
    <PrismThemeProvider>
      <TertiaryNavigationRoot
        aria-label={ariaLabel}
        data-testid={TERTIARY_NAVIGATION_TEST_IDS.root}
        zoomLevel={zoomLevel}>
        <TertiaryNavigationItemsArea>
          {scrollState.hasOverflow && (
            <TertiaryNavigationScrollButton
              aria-label="Scroll tertiary navigation tabs left"
              data-testid={TERTIARY_NAVIGATION_TEST_IDS.itemsScrollLeft}
              disabled={!scrollState.canScrollLeft}
              onClick={() => handleItemsScroll('left')}
              type="button">
              <ChevronLeftIcon fontSize="inherit"/>
            </TertiaryNavigationScrollButton>
          )}
          <TertiaryNavigationItems
            ref={itemsViewportRef}
            data-testid={TERTIARY_NAVIGATION_TEST_IDS.items}
            role="tablist">
            <TertiaryNavigationActiveIndicator ref={setActiveIndicatorRef} aria-hidden="true" />
            {items.map(item => (
              <TertiaryNavigationItem
                key={item.value}
                ref={setItemRef(item.value)}
                closeAriaLabel={item.closeAriaLabel}
                disabled={item.disabled}
                focusable={focusableValue === item.value}
                hasError={item.hasError}
                isClosable={item.isClosable}
                label={item.label}
                onKeyboardNavigate={handleKeyboardNavigate}
                onItemClose={onItemClose ? event => onItemClose(event, item) : undefined}
                onItemMenuClick={onItemMenuClick ? event => onItemMenuClick(event, item) : undefined}
                onSelect={() => onChange?.(item.value)}
                selected={value === item.value}
                showMenu={item.showMenu}
                value={item.value}
                zoomLevel={zoomLevel}
              />
            ))}
          </TertiaryNavigationItems>
          {scrollState.hasOverflow && (
            <TertiaryNavigationScrollButton
              aria-label="Scroll tertiary navigation tabs right"
              data-testid={TERTIARY_NAVIGATION_TEST_IDS.itemsScrollRight}
              disabled={!scrollState.canScrollRight}
              onClick={() => handleItemsScroll('right')}
              type="button">
              <ChevronRightIcon fontSize="inherit"/>
            </TertiaryNavigationScrollButton>
          )}
          {leftActions && (
            <TertiaryNavigationLeftActions data-testid={TERTIARY_NAVIGATION_TEST_IDS.leftActions}>
              {leftActions}
            </TertiaryNavigationLeftActions>
          )}
        </TertiaryNavigationItemsArea>
        {actions && (
          <TertiaryNavigationActions data-testid={TERTIARY_NAVIGATION_TEST_IDS.actions}>
            {actions}
          </TertiaryNavigationActions>
        )}
      </TertiaryNavigationRoot>
    </PrismThemeProvider>
  );
};

export const TertiaryNavigationSkeleton = ({
  actionCount = 5,
  ariaLabel = 'Loading tertiary navigation',
  itemCount = 4,
  showActions = true,
  showLeftAction = true,
  zoomLevel = DEFAULT_ZOOM_LEVEL,
}: TertiaryNavigationSkeletonProps) => (
  <PrismThemeProvider>
    <TertiaryNavigationRoot
      aria-label={ariaLabel}
      data-testid={TERTIARY_NAVIGATION_TEST_IDS.skeleton}
      zoomLevel={zoomLevel}>
      <TertiaryNavigationItemsArea>
        <TertiaryNavigationItems>
          {createSkeletonItems(itemCount).map(index => (
            <Skeleton
              key={index}
              height={getZoomAdjustedPx(32, zoomLevel)}
              radius={getZoomAdjustedPx(4, zoomLevel)}
              width={getZoomAdjustedPx(index === 0 ? 124 : 104, zoomLevel)}
            />
          ))}
        </TertiaryNavigationItems>
        {showLeftAction && (
          <TertiaryNavigationLeftActions>
            <Skeleton
              height={getZoomAdjustedPx(32, zoomLevel)}
              radius={getZoomAdjustedPx(4, zoomLevel)}
              width={getZoomAdjustedPx(32, zoomLevel)}
            />
          </TertiaryNavigationLeftActions>
        )}
      </TertiaryNavigationItemsArea>
      {showActions && (
        <TertiaryNavigationActions>
          {createSkeletonItems(actionCount).map(index => (
            <Skeleton
              key={index}
              height={getZoomAdjustedPx(24, zoomLevel)}
              radius={getZoomAdjustedPx(4, zoomLevel)}
              variant="pill"
              width={getZoomAdjustedPx(index === 1 ? 104 : 24, zoomLevel)}
            />
          ))}
        </TertiaryNavigationActions>
      )}
    </TertiaryNavigationRoot>
  </PrismThemeProvider>
);

export const TertiaryNavigationIconButton = ({
  ariaLabel,
  icon,
  onClick,
  tooltipTitle = ariaLabel,
  variant = 'toolbar',
  ...props
}: TertiaryNavigationIconButtonProps) => {
  const suppressTooltipUntilCloseRef = useRef(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const handleTooltipOpen = () => {
    if (suppressTooltipUntilCloseRef.current) {
      return;
    }

    setIsTooltipOpen(true);
  };

  const handleTooltipClose = () => {
    suppressTooltipUntilCloseRef.current = false;
    setIsTooltipOpen(false);
  };

  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    suppressTooltipUntilCloseRef.current = true;
    setIsTooltipOpen(false);
    onClick?.(event);
  };

  const button = (
    <TertiaryNavigationIconButtonRoot
      aria-label={ariaLabel}
      iconButtonVariant={variant}
      disableRipple
      onClick={handleButtonClick}
      {...props}>
      {icon}
    </TertiaryNavigationIconButtonRoot>
  );

  return (
    <Tooltip
      onClose={handleTooltipClose}
      onOpen={handleTooltipOpen}
      open={isTooltipOpen}
      placement="bottom"
      title={tooltipTitle}>
      <TertiaryNavigationIconTooltipAnchor>
        {button}
      </TertiaryNavigationIconTooltipAnchor>
    </Tooltip>
  );
};

export const TertiaryNavigationSearchSuggestionPill = ({
  children,
  tone = 'neutral',
  tooltipTitle,
  ...props
}: TertiaryNavigationSearchSuggestionPillProps) => {
  const pillRef = useRef<HTMLSpanElement | null>(null);
  const pill = (
    <TertiaryNavigationSearchSuggestionPillRoot
      {...props}
      ref={pillRef}
      data-tertiary-navigation-search-suggestion-pill="true"
      data-tertiary-navigation-search-suggestion-pill-tone={tone}>
      {children}
    </TertiaryNavigationSearchSuggestionPillRoot>
  );

  if (!tooltipTitle) {
    return pill;
  }

  return (
    <OverflowTooltip measureRef={pillRef} placement="bottom" title={tooltipTitle}>
      {pill}
    </OverflowTooltip>
  );
};

const TertiaryNavigationSearchSuggestionLabelText = ({
  highlightText,
  label,
}: {
  highlightText: string;
  label: ReactNode;
}) => {
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const labelNode = (
    <TertiaryNavigationSearchSuggestionLabel ref={labelRef}>
      {typeof label === 'string' ? renderHighlightedSearchText(label, highlightText) : label}
    </TertiaryNavigationSearchSuggestionLabel>
  );

  if (typeof label !== 'string') {
    return labelNode;
  }

  return (
    <OverflowTooltip measureRef={labelRef} placement="bottom" title={label}>
      {labelNode}
    </OverflowTooltip>
  );
};

export const TertiaryNavigationSearch = ({
  ariaLabel,
  collapsedAriaLabel,
  collapsedTooltipTitle,
  clearAriaLabel = 'Clear search',
  commitMode = 'change',
  commitDelayMs = 0,
  collapsible = false,
  disabled,
  isSuggestionsLoading = false,
  onChange,
  onInputValueChange,
  onSuggestionSelect,
  placeholder,
  showEmptySuggestions = false,
  startIcon = <SearchIcon fontSize="inherit" />,
  submitAriaLabel = 'Submit search',
  submitHint = 'Press ↵',
  suggestions = [],
  suggestionsEmptyLabel = 'No matching results',
  suggestionsLoadingLabel = 'Searching documents...',
  testId,
  value = '',
}: TertiaryNavigationSearchProps) => {
  const theme = useTheme();
  const listboxId = useId();
  const [isFocused, setIsFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(() => !collapsible || value.trim().length > 0);
  const [isCollapsing, setIsCollapsing] = useState(false);
  const [areSuggestionsDismissed, setAreSuggestionsDismissed] = useState(false);
  const [submitValue, setSubmitValue] = useState(value);
  const [isSubmitHintActive, setIsSubmitHintActive] = useState(false);
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const collapseTimeoutRef = useRef<number | null>(null);
  const submitHintActiveTimeoutRef = useRef<number | null>(null);
  const shouldCommitOnSubmit = commitMode === 'submit';
  const {
    setValue: setSearchValue,
    value: delayedSearchValue,
  } = useDelayedCommitValue({
    commitDelayMs,
    onCommit: onChange,
    value,
  });
  const searchValue = shouldCommitOnSubmit ? submitValue : delayedSearchValue;
  const hasSearchValue = searchValue.trim().length > 0;
  const hasCommittedSearchValue = value.trim().length > 0;
  const hasSuggestionSearch = searchValue.trim().length >= 2;
  const shouldShowSubmitHint = Boolean(
    shouldCommitOnSubmit
      && isFocused
      && !disabled
      && !isSuggestionsLoading
      && submitHint
  );
  const shouldShowSuggestions = Boolean(
    isFocused
      && !disabled
      && onSuggestionSelect
      && hasSuggestionSearch
      && !areSuggestionsDismissed
      && !isSuggestionsLoading
      && (suggestions.length > 0 || showEmptySuggestions)
  );
  const shouldShowClearButton = Boolean(!disabled && onChange && (hasSearchValue || hasCommittedSearchValue));
  const shouldShowSearchLoader = isSuggestionsLoading;
  const shouldShowSearchClearButton = shouldShowClearButton && !isSuggestionsLoading;
  const shouldShowTrailingActions = shouldShowSearchLoader || shouldShowSearchClearButton || shouldShowSubmitHint;
  const shouldShowCollapsedSearch = collapsible && !isExpanded && !hasCommittedSearchValue && !isCollapsing;
  const collapseDurationMs = theme.transitions.duration.short;

  // In submit mode, mirror the committed external value back into the local input state.
  useEffect(() => {
    if (shouldCommitOnSubmit) {
      setSubmitValue(value);
    }
  }, [shouldCommitOnSubmit, value]);

  // Keep the collapsible search open once it has committed or in-progress text.
  useEffect(() => {
    if (!collapsible) {
      setIsExpanded(true);
      setIsCollapsing(false);
      return;
    }

    if (value.trim() || searchValue.trim()) {
      setIsCollapsing(false);
      setIsExpanded(true);
    }
  }, [collapsible, searchValue, value]);

  // Keep the end of long focused search values visible as trailing controls appear or disappear.
  useLayoutEffect(() => {
    const input = inputRef.current;

    if (
      !isFocused
      || !input
      || typeof document === 'undefined'
      || typeof window === 'undefined'
      || document.activeElement !== input
    ) {
      return undefined;
    }

    const isCaretAtEnd = input.selectionStart === searchValue.length && input.selectionEnd === searchValue.length;

    if (!isCaretAtEnd) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => {
      input.scrollLeft = input.scrollWidth;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [
    isFocused,
    searchValue,
    shouldShowSearchClearButton,
    shouldShowSearchLoader,
    shouldShowSubmitHint,
  ]);

  // Reset keyboard suggestion focus whenever the query text or suggestion set changes.
  useEffect(() => {
    setHighlightedSuggestionIndex(-1);
  }, [searchValue, suggestions]);

  // Clear the transient keyboard-submit feedback timer when the search unmounts.
  useEffect(() => () => {
    if (collapseTimeoutRef.current !== null) {
      window.clearTimeout(collapseTimeoutRef.current);
    }

    if (submitHintActiveTimeoutRef.current !== null) {
      window.clearTimeout(submitHintActiveTimeoutRef.current);
    }
  }, []);

  const commitValue = useCallback((nextValue: string) => {
    onChange?.(nextValue);
  }, [onChange]);

  const activateSubmitHint = useCallback(() => {
    if (!shouldShowSubmitHint) {
      return;
    }

    if (submitHintActiveTimeoutRef.current !== null) {
      window.clearTimeout(submitHintActiveTimeoutRef.current);
    }

    setIsSubmitHintActive(true);
    submitHintActiveTimeoutRef.current = window.setTimeout(() => {
      setIsSubmitHintActive(false);
      submitHintActiveTimeoutRef.current = null;
    }, TERTIARY_NAVIGATION_SEARCH_SUBMIT_HINT_ACTIVE_MS);
  }, [shouldShowSubmitHint]);

  const submitSearchValue = useCallback((nextValue: string) => {
    commitValue(nextValue);
    setAreSuggestionsDismissed(true);
    setHighlightedSuggestionIndex(-1);
  }, [commitValue]);

  const clearSearchValue = useCallback(() => {
    if (shouldCommitOnSubmit) {
      setSubmitValue('');
    } else {
      setSearchValue('');
    }

    onInputValueChange?.('');
    commitValue('');
    setIsFocused(true);
    setAreSuggestionsDismissed(true);
    setHighlightedSuggestionIndex(-1);
    inputRef.current?.focus();
  }, [commitValue, onInputValueChange, setSearchValue, shouldCommitOnSubmit]);

  const handleSearchValueChange = useCallback((nextValue: string) => {
    // Enter/Escape can dismiss suggestions while the input remains focused.
    // Any new typing should reopen suggestions without requiring blur/refocus.
    setIsFocused(true);
    setAreSuggestionsDismissed(false);

    if (shouldCommitOnSubmit) {
      setSubmitValue(nextValue);
      onInputValueChange?.(nextValue);
      return;
    }

    setSearchValue(nextValue);
    onInputValueChange?.(nextValue);
  }, [onInputValueChange, setSearchValue, shouldCommitOnSubmit]);

  const handleSuggestionSelect = useCallback((suggestionIndex: number) => {
    const suggestion = suggestions[suggestionIndex];

    if (!suggestion) {
      return;
    }

    onSuggestionSelect?.(suggestion);
    setSubmitValue(suggestion.inputValue ?? String(suggestion.label));
    setAreSuggestionsDismissed(true);
    setHighlightedSuggestionIndex(-1);
  }, [onSuggestionSelect, suggestions]);

  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (!shouldShowSuggestions) {
      if (event.key === 'Enter' && shouldCommitOnSubmit) {
        event.preventDefault();
        activateSubmitHint();
        submitSearchValue(searchValue);
      }

      return;
    }

    if (event.key === 'ArrowDown' && suggestions.length > 0) {
      event.preventDefault();
      setHighlightedSuggestionIndex(currentIndex => (
        currentIndex + 1 >= suggestions.length ? 0 : currentIndex + 1
      ));
      return;
    }

    if (event.key === 'ArrowUp' && suggestions.length > 0) {
      event.preventDefault();
      setHighlightedSuggestionIndex(currentIndex => (
        currentIndex <= 0 ? suggestions.length - 1 : currentIndex - 1
      ));
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setAreSuggestionsDismissed(true);
      setHighlightedSuggestionIndex(-1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      if (highlightedSuggestionIndex >= 0 && suggestions[highlightedSuggestionIndex]) {
        handleSuggestionSelect(highlightedSuggestionIndex);
        return;
      }

      activateSubmitHint();
      submitSearchValue(searchValue);
    }
  }, [
    activateSubmitHint,
    handleSuggestionSelect,
    highlightedSuggestionIndex,
    searchValue,
    shouldCommitOnSubmit,
    shouldShowSuggestions,
    submitSearchValue,
    suggestions,
  ]);

  const handleBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    setIsFocused(false);
    setAreSuggestionsDismissed(false);
    setHighlightedSuggestionIndex(-1);
    if (collapsible && !searchValue.trim()) {
      if (collapseTimeoutRef.current !== null) {
        window.clearTimeout(collapseTimeoutRef.current);
      }

      setIsCollapsing(true);
      collapseTimeoutRef.current = window.setTimeout(() => {
        setIsExpanded(false);
        setIsCollapsing(false);
        collapseTimeoutRef.current = null;
      }, collapseDurationMs);
    }
  }, [collapseDurationMs, collapsible, searchValue]);

  const openCollapsedSearch = useCallback(() => {
    if (collapseTimeoutRef.current !== null) {
      window.clearTimeout(collapseTimeoutRef.current);
      collapseTimeoutRef.current = null;
    }

    setIsCollapsing(false);
    setIsExpanded(true);
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  let suggestionsContent: ReactNode = (
    <TertiaryNavigationSearchSuggestionsStatus>
      {suggestionsEmptyLabel}
    </TertiaryNavigationSearchSuggestionsStatus>
  );

  if (isSuggestionsLoading) {
    suggestionsContent = (
      <TertiaryNavigationSearchSuggestionsStatus>
        {suggestionsLoadingLabel}
      </TertiaryNavigationSearchSuggestionsStatus>
    );
  } else if (suggestions.length > 0) {
    suggestionsContent = suggestions.map((suggestion, index) => {
      const optionId = `${listboxId}-option-${index}`;

      return (
        <TertiaryNavigationSearchSuggestionItem
          key={suggestion.value}
          aria-selected={highlightedSuggestionIndex === index}
          data-highlighted={highlightedSuggestionIndex === index}
          id={optionId}
          onClick={() => handleSuggestionSelect(index)}
          onMouseMove={() => setHighlightedSuggestionIndex(index)}
          role="option"
          tabIndex={-1}
          type="button">
          <TertiaryNavigationSearchSuggestionIcon>
            {suggestion.startIcon}
          </TertiaryNavigationSearchSuggestionIcon>
          <TertiaryNavigationSearchSuggestionContent>
            <TertiaryNavigationSearchSuggestionLabelText highlightText={searchValue} label={suggestion.label} />
            {suggestion.metadata && (
              <TertiaryNavigationSearchSuggestionMetadata>
                {suggestion.metadata}
              </TertiaryNavigationSearchSuggestionMetadata>
            )}
            {suggestion.description && (
              <TertiaryNavigationSearchSuggestionDescription>
                {suggestion.description}
              </TertiaryNavigationSearchSuggestionDescription>
            )}
          </TertiaryNavigationSearchSuggestionContent>
        </TertiaryNavigationSearchSuggestionItem>
      );
    });
  }

  const activeSuggestionId = shouldShowSuggestions && highlightedSuggestionIndex >= 0
    ? `${listboxId}-option-${highlightedSuggestionIndex}`
    : undefined;

  if (shouldShowCollapsedSearch) {
    return (
      <Tooltip placement="bottom" title={collapsedTooltipTitle ?? ariaLabel}>
        <TertiaryNavigationIconTooltipAnchor>
          <TertiaryNavigationIconButtonRoot
            aria-label={collapsedAriaLabel ?? ariaLabel}
            data-testid={testId ? `${testId}-trigger` : undefined}
            disabled={disabled}
            disableRipple
            iconButtonVariant="toolbar"
            onClick={openCollapsedSearch}
            type="button">
            {startIcon}
          </TertiaryNavigationIconButtonRoot>
        </TertiaryNavigationIconTooltipAnchor>
      </Tooltip>
    );
  }

  return (
    <TertiaryNavigationSearchRoot
      data-collapsing={isCollapsing ? 'true' : undefined}
      onBlur={handleBlur}
      onFocus={() => {
        setIsFocused(true);
        setAreSuggestionsDismissed(false);
      }}>
      <TertiaryNavigationSearchIcon aria-hidden="true">
        {startIcon}
      </TertiaryNavigationSearchIcon>
      <TertiaryNavigationSearchInput
        aria-label={ariaLabel}
        disabled={disabled}
        inputRef={inputRef}
        inputProps={{
          'aria-activedescendant': activeSuggestionId,
          'aria-autocomplete': onSuggestionSelect ? 'list' : undefined,
          'aria-busy': isSuggestionsLoading || undefined,
          'aria-controls': shouldShowSuggestions ? listboxId : undefined,
          'aria-expanded': onSuggestionSelect ? shouldShowSuggestions : undefined,
          'data-testid': testId,
          role: onSuggestionSelect ? 'combobox' : undefined,
        }}
        onChange={event => handleSearchValueChange(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        value={searchValue}
      />
      {shouldShowTrailingActions && (
        <TertiaryNavigationSearchTrailingActions data-tertiary-navigation-search-trailing-actions="true">
          {shouldShowSearchLoader && (
            <TertiaryNavigationSearchLoader aria-hidden="true">
              <CircularProgress color="inherit" size="inherit" thickness={5} />
            </TertiaryNavigationSearchLoader>
          )}
          {shouldShowSearchClearButton && (
            <Tooltip placement="bottom" title={clearAriaLabel}>
              <TertiaryNavigationSearchClearButton
                aria-label={clearAriaLabel}
                onClick={clearSearchValue}
                onMouseDown={event => event.preventDefault()}
                type="button">
                <CloseIcon fontSize="inherit" />
              </TertiaryNavigationSearchClearButton>
            </Tooltip>
          )}
          {shouldShowSubmitHint && (
            <Tooltip placement="bottom" title={submitAriaLabel}>
              <TertiaryNavigationSearchSubmitHint
                aria-label={submitAriaLabel}
                data-active={isSubmitHintActive || undefined}
                onClick={() => submitSearchValue(searchValue)}
                onMouseDown={event => event.preventDefault()}
                type="button">
                {submitHint}
              </TertiaryNavigationSearchSubmitHint>
            </Tooltip>
          )}
        </TertiaryNavigationSearchTrailingActions>
      )}
      {shouldShowSuggestions && (
        <TertiaryNavigationSearchSuggestions
          id={listboxId}
          onMouseDown={event => event.preventDefault()}
          role="listbox">
          {suggestionsContent}
        </TertiaryNavigationSearchSuggestions>
      )}
    </TertiaryNavigationSearchRoot>
  );
};

export const TertiaryNavigationAddButton = (
  props: Omit<TertiaryNavigationIconButtonProps, 'ariaLabel' | 'icon' | 'variant'>
) => (
  <TertiaryNavigationIconButton ariaLabel="Add view" icon={<AddIcon fontSize="inherit"/>} variant="add" {...props} />
);

export const TertiaryNavigationAiButton = ({
  ariaLabel = 'AI actions',
  icon = <TertiaryNavigationAiIcon alt="" aria-hidden
    src={SCALAR_AI_ICON_SRC}/>,
  ...props
}: TertiaryNavigationAiButtonProps) => {
  const button = (
    <TertiaryNavigationAiButtonRoot
      aria-label={ariaLabel}
      data-testid={TERTIARY_NAVIGATION_TEST_IDS.aiButton}
      disableRipple
      {...props}>
      {icon}
    </TertiaryNavigationAiButtonRoot>
  );

  return (
    <Tooltip placement="bottom" title={ariaLabel}>
      <TertiaryNavigationIconTooltipAnchor>
        {button}
      </TertiaryNavigationIconTooltipAnchor>
    </Tooltip>
  );
};

export const TertiaryNavigationCheckboxAction = ({
  disabled,
  inputProps,
  label,
  ...props
}: TertiaryNavigationCheckboxActionProps) => (
  <TertiaryNavigationCheckboxActionRoot isDisabled={disabled}>
    <Checkbox
      disabled={disabled}
      inputProps={{
        'aria-label': typeof label === 'string' ? label : undefined,
        ...inputProps,
      }}
      visualVariant="selection"
      {...props}
    />
    <TertiaryNavigationCheckboxActionLabel>{label}</TertiaryNavigationCheckboxActionLabel>
  </TertiaryNavigationCheckboxActionRoot>
);

export const TertiaryNavigationCurrencySelector = ({
  currency,
  currencyAriaLabel = 'Select currency',
  onCurrencyClick,
  onUnitClick,
  unit,
  unitAriaLabel = 'Select display unit',
  ...props
}: TertiaryNavigationCurrencySelectorProps) => (
  <TertiaryNavigationCurrencySelectorRoot
    data-testid={TERTIARY_NAVIGATION_TEST_IDS.currencySelector}
    {...props}>
    <TertiaryNavigationCurrencySegment
      aria-label={currencyAriaLabel}
      data-testid={TERTIARY_NAVIGATION_TEST_IDS.currencyValue}
      disableRipple
      onClick={onCurrencyClick}
      segment="currency">
      <OverflowTooltip title={getTextTooltipTitle(currency)}>
        <TertiaryNavigationCurrencyValue>{currency}</TertiaryNavigationCurrencyValue>
      </OverflowTooltip>
      <ArrowDropDownIcon fontSize="inherit"/>
    </TertiaryNavigationCurrencySegment>
    <TertiaryNavigationCurrencySegment
      aria-label={unitAriaLabel}
      data-testid={TERTIARY_NAVIGATION_TEST_IDS.currencyUnit}
      disableRipple
      onClick={onUnitClick}
      segment="unit">
      <OverflowTooltip title={getTextTooltipTitle(unit)}>
        <TertiaryNavigationCurrencyUnit>{unit}</TertiaryNavigationCurrencyUnit>
      </OverflowTooltip>
      <ArrowDropDownIcon fontSize="inherit"/>
    </TertiaryNavigationCurrencySegment>
  </TertiaryNavigationCurrencySelectorRoot>
);

export const TertiaryNavigationActionButton = ({
  children,
  'data-testid': dataTestId,
  disabled,
  endIcon,
  errorLabel = 'Error',
  isSaving = false,
  onClick,
  saveFailed = false,
  savedLabel = 'Saved',
  savingLabel = 'Saving...',
  startIcon,
  variant = 'secondary',
  ...props
}: TertiaryNavigationActionButtonProps) => {
  const { handleClick, isActionDisabled, visualState } = useNavigationActionFeedback({
    disabled,
    isSaving,
    onClick,
    saveFailed,
  });
  const {
    contentWidth,
    errorContentRef,
    idleContentRef,
    savedContentRef,
    savingContentRef,
  } = useNavigationActionContentWidth(visualState, {
    errorLabel,
    idleContent: children,
    savedLabel,
    savingLabel,
  });
  const statusIcon = getTertiaryNavigationActionStatusIcon(visualState);

  return (
    <TertiaryNavigationActionButtonRoot
      {...props}
      actionButtonState={visualState}
      actionButtonVariant={variant}
      aria-busy={visualState === 'saving' || undefined}
      aria-disabled={isActionDisabled || undefined}
      data-testid={dataTestId ?? TERTIARY_NAVIGATION_TEST_IDS.actionButton}
      disabled={disabled}
      disableRipple
      endIcon={statusIcon ?? endIcon}
      onClick={handleClick}
      startIcon={visualState === 'idle' ? startIcon : undefined}>
      <TertiaryNavigationActionContentSlots
        contentWidth={contentWidth}
        errorContentRef={errorContentRef}
        errorLabel={errorLabel}
        idleContentRef={idleContentRef}
        savedContentRef={savedContentRef}
        savedLabel={savedLabel}
        savingContentRef={savingContentRef}
        savingLabel={savingLabel}
        visualState={visualState}>
        {children}
      </TertiaryNavigationActionContentSlots>
    </TertiaryNavigationActionButtonRoot>
  );
};

export const TertiaryNavigationActionTransition = ({
  children,
  isVisible = true,
  style,
  ...props
}: TertiaryNavigationActionTransitionProps) => {
  const contentRef = useRef<HTMLSpanElement | null>(null);
  const [hasEntered, setHasEntered] = useState(false);
  const [contentWidth, setContentWidth] = useState(0);
  const isRenderedVisible = isVisible && hasEntered;
  const rootStyle = {
    ...style,
    '--tertiary-navigation-action-transition-width': `${contentWidth}px`,
  } as CSSProperties & Record<'--tertiary-navigation-action-transition-width', string>;

  const updateContentWidth = useCallback(() => {
    const content = contentRef.current;

    if (!content) {
      return;
    }

    const nextContentWidth = Math.ceil(content.scrollWidth || content.getBoundingClientRect().width);

    setContentWidth(currentContentWidth => (
      currentContentWidth === nextContentWidth ? currentContentWidth : nextContentWidth
    ));
  }, []);

  // Keep dynamic action width in sync as labels/counts change so hide/show transitions mirror search.
  useLayoutEffect(() => {
    updateContentWidth();

    const content = contentRef.current;

    if (!content || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(updateContentWidth);
    resizeObserver.observe(content);

    return () => resizeObserver.disconnect();
  }, [children, updateContentWidth]);

  // Start newly mounted dynamic actions collapsed for one frame so enter transitions are visible.
  useEffect(() => {
    if (!isVisible) {
      setHasEntered(false);
      return undefined;
    }

    if (typeof window === 'undefined') {
      setHasEntered(true);
      return undefined;
    }

    const animationFrameId = window.requestAnimationFrame(() => setHasEntered(true));

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [isVisible]);

  return (
    <TertiaryNavigationActionTransitionRoot
      aria-hidden={isRenderedVisible ? undefined : true}
      isVisible={isRenderedVisible}
      style={rootStyle}
      {...props}>
      <TertiaryNavigationActionTransitionContent ref={contentRef}>
        {children}
      </TertiaryNavigationActionTransitionContent>
    </TertiaryNavigationActionTransitionRoot>
  );
};

export const TertiaryNavigationSplitActionButton = ({
  children,
  'data-testid': dataTestId,
  errorLabel = 'Error',
  isSaving = false,
  locked,
  disabled,
  mainDisabled = false,
  menuAriaLabel,
  menuDisabled = false,
  onMenuClick,
  onClick,
  saveFailed = false,
  savedLabel = 'Saved',
  savingLabel = 'Saving...',
  variant = 'secondary',
  ...props
}: TertiaryNavigationSplitActionButtonProps) => {
  const isMainDisabled = Boolean(disabled || mainDisabled);
  const { handleClick, isActionDisabled, visualState } = useNavigationActionFeedback({
    disabled: isMainDisabled,
    isSaving,
    onClick,
    saveFailed,
  });
  const {
    contentWidth,
    errorContentRef,
    idleContentRef,
    savedContentRef,
    savingContentRef,
  } = useNavigationActionContentWidth(visualState, {
    errorLabel,
    idleContent: children,
    savedLabel,
    savingLabel,
  });

  const splitMenuIcon = (() => {
    const statusIcon = getTertiaryNavigationActionStatusIcon(visualState);
    if (statusIcon) {
      return statusIcon;
    }

    return <ArrowDropDownIcon fontSize="inherit"/>;
  })();
  const isMenuActionDisabled = Boolean(disabled || menuDisabled || isSaving || visualState !== 'idle');
  const isMainFinalAllocationLocked = Boolean(locked && isMainDisabled);
  const isMenuFinalAllocationLocked = Boolean(locked && isMenuActionDisabled);
  const handleMenuClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    if (isMenuActionDisabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    onMenuClick?.(event);
  }, [isMenuActionDisabled, onMenuClick]);

  return (
    <TertiaryNavigationSplitActionRoot data-testid={TERTIARY_NAVIGATION_TEST_IDS.splitActionButton}>
      <TertiaryNavigationSplitMainButton
        {...props}
        actionButtonVariant={variant}
        aria-busy={visualState === 'saving' || undefined}
        aria-disabled={isActionDisabled || undefined}
        data-testid={dataTestId ?? TERTIARY_NAVIGATION_TEST_IDS.splitActionMainButton}
        disabled={isMainDisabled}
        disableRipple
        isFinalAllocationLocked={isMainFinalAllocationLocked}
        isLocked={locked}
        onClick={handleClick}
        splitActionState={visualState}>
        <TertiaryNavigationActionContentSlots
          contentWidth={contentWidth}
          errorContentRef={errorContentRef}
          errorLabel={errorLabel}
          idleContentRef={idleContentRef}
          savedContentRef={savedContentRef}
          savedLabel={savedLabel}
          savingContentRef={savingContentRef}
          savingLabel={savingLabel}
          visualState={visualState}>
          {children}
        </TertiaryNavigationActionContentSlots>
      </TertiaryNavigationSplitMainButton>
      {onMenuClick && (
        <TertiaryNavigationSplitMenuButton
          actionButtonVariant={variant}
          aria-disabled={isMenuActionDisabled || undefined}
          aria-label={menuAriaLabel ?? `${typeof children === 'string' ? children : 'Action'} options`}
          data-testid={TERTIARY_NAVIGATION_TEST_IDS.splitActionMenuButton}
          disabled={isMenuActionDisabled}
          disableRipple
          isFinalAllocationLocked={isMenuFinalAllocationLocked}
          isLocked={locked}
          onClick={handleMenuClick}
          splitActionState={visualState}
          tabIndex={isMenuActionDisabled && !disabled ? -1 : undefined}
          type="button">
          {splitMenuIcon}
        </TertiaryNavigationSplitMenuButton>
      )}
    </TertiaryNavigationSplitActionRoot>
  );
};

export {
  TERTIARY_NAVIGATION_ACTION_UNMOUNT_DELAY_MS,
  TERTIARY_NAVIGATION_ACTION_VISIBILITY_TRANSITION_MS,
} from './TertiaryNavigation.styles';

export default TertiaryNavigation;
